import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { buildApiUrl } from '../config/api';
import { getAuthHeaders } from '../utils/apiAuth';

/**
 * Fiche d'un projet lié à Synapse (`/synapse/projects/:projectId`), destination de l'ouverture
 * depuis Synapse. Lue par le seul propriétaire natif attesté via le socle
 * (`GET /api/synapse/suite/projects/:id`). Ses sources ne s'affichent que pendant l'ouverture qui
 * les a autorisées (`?launch=`), jamais à partir d'une session native isolée : c'est le hub qui
 * porte le droit Synapse. Leur ajout et leur retrait restent au mode dossiers (deux sessions).
 * Cette page ne remplace pas l'ancienne page « Projets », fermée dès qu'une liaison existe.
 */
export default function SynapseProject({ fetcher = fetch }) {
  const { projectId } = useParams();
  const [params] = useSearchParams();
  const launchId = params.get('launch');
  const [state, setState] = useState({ status: 'working' });
  // Les sources ne sont lisibles que pendant l'ouverture qui les a autorisées (cf. backend).
  const [sources, setSources] = useState({ status: launchId ? 'working' : 'absent' });
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const response = await fetcher(buildApiUrl(`synapse/suite/projects/${encodeURIComponent(projectId)}`), { headers: getAuthHeaders() });
        if (cancelled) return;
        if (response.status === 401) { setState({ status: 'error', message: 'Session Biblio requise : ouvrez ce projet depuis Synapse.' }); return; }
        if (response.status === 404) { setState({ status: 'error', message: 'Projet lié introuvable pour ce compte Biblio.' }); return; }
        if (!response.ok) { setState({ status: 'error', message: 'Projet lié indisponible pour le moment.' }); return; }
        setState({ status: 'done', project: await response.json() });
      } catch (error) {
        if (!cancelled) setState({ status: 'error', message: 'Projet lié indisponible pour le moment.' });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [fetcher, projectId]);
  useEffect(() => {
    if (!launchId) return undefined;
    let cancelled = false;
    const run = async () => {
      try {
        const response = await fetcher(buildApiUrl(`synapse/suite/launches/${encodeURIComponent(launchId)}/sources`), { headers: getAuthHeaders() });
        if (cancelled) return;
        if (!response.ok) { setSources({ status: 'expired' }); return; }
        const body = await response.json();
        setSources({ status: 'done', items: body.sources, total: body.total });
      } catch (error) {
        if (!cancelled) setSources({ status: 'expired' });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [fetcher, launchId]);
  return (
    <main style={{ maxWidth: 640, margin: '0 auto', padding: 24 }} aria-live="polite">
      {state.status === 'working' && <p role="status">Chargement du projet lié…</p>}
      {state.status === 'error' && <p role="alert">{state.message}</p>}
      {state.status === 'done' && (
        <article>
          <p style={{ textTransform: 'uppercase', fontSize: 12, opacity: 0.7 }}>Projet lié à Synapse</p>
          <h1>{state.project.name}</h1>
          <dl>
            <dt>Espace Synapse</dt><dd>{state.project.scopeContext.workspaceId}</dd>
            <dt>Organisation</dt><dd>{state.project.scopeContext.companyId}</dd>
            <dt>Dossier</dt><dd>{state.project.scopeContext.scopeId}</dd>
            <dt>État</dt><dd>{state.project.syncStatus === 'synced' ? 'Rattaché et confirmé par Synapse' : 'Rattachement en attente de confirmation'}</dd>
          </dl>
          <section aria-label="Sources du projet">
            <h2>Sources</h2>
            {sources.status === 'working' && <p role="status">Lecture des sources…</p>}
            {sources.status === 'done' && sources.total === 0 && <p>Aucune source n’est encore rattachée à ce projet.</p>}
            {sources.status === 'done' && sources.total > 0 && (
              <ul>{sources.items.map((s) => <li key={s.bookId}>{s.title}{s.author ? ` — ${s.author}` : ''}</li>)}</ul>
            )}
            {sources.status !== 'done' && sources.status !== 'working' && (
              <p>Les sources s’affichent pendant l’ouverture depuis Synapse. Rouvrez le projet depuis Synapse pour les consulter.</p>
            )}
          </section>
          <p>L’ajout et le retrait de sources restent gérés par le mode dossiers de Biblio-Tech. Cette fiche ne montre aucun contenu de livre.</p>
          <p><Link to="/">Retour à Biblio-Tech</Link></p>
        </article>
      )}
    </main>
  );
}
